import type { RootState } from '@reducers/index';
import { updatePage } from '@reducers/search';
import { Button } from '@sbt-web/ui';
import { LISTING_PAGE_LIMIT } from '@shared/constants';
import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';

import ownClasses from './pagination.module.scss';

interface StateProps {
  currentPage: number;
  lastPage: number;
}

interface DispatchProps {
  changePage: (page: number) => void;
}

type Props = StateProps & DispatchProps;

enum ButtonDirection {
  PREV = 'left-arrow',
  NEXT = 'right-arrow',
}

const ArrowLeft: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
    <path
      fillRule="evenodd"
      d="M9.414 12l6.293-6.293a1 1 0 10-1.414-1.414l-7 7a1 1 0 000 1.414l7 7a1 1 0 001.414-1.414L9.414 12z"
    />
  </svg>
);

const ArrowRight: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
    <path
      fillRule="evenodd"
      d="M14.586 12L8.293 5.707a1 1 0 111.414-1.414l7 7a1 1 0 010 1.414l-7 7a1 1 0 01-1.414-1.414L14.586 12z"
    />
  </svg>
);

type PageClick = (e: React.FormEvent, page: number) => void;

const Placeholder = () => (
  <span
    className={classnames(
      'button-text',
      ownClasses['button-like'],
      ownClasses.placeholder
    )}
  >
    …
  </span>
);

type PaginationArrowProps = {
  direction: ButtonDirection;
  onClick: PageClick;
  page: number | null;
  disabled: boolean;
};

const PaginationArrow: React.FunctionComponent<PaginationArrowProps> = ({
  direction,
  onClick,
  page,
  disabled = false,
}) => {
  const icon =
    direction === ButtonDirection.NEXT ? <ArrowRight /> : <ArrowLeft />;

  return (
    <Button
      design="outline"
      icon={icon}
      disabled={disabled}
      onClick={(e) => {
        if (page) {
          onClick(e, page);
        }
      }}
      aria-label={
        direction === ButtonDirection.NEXT
          ? 'Andare alla prossima pagina'
          : 'Andare alla pagina precedente'
      }
    />
  );
};

interface PageNumberProps {
  page: number;
  onClick: PageClick;
}

const PageNumber: React.FunctionComponent<PageNumberProps> = ({
  page,
  onClick,
}) => {
  return (
    <Button
      design="text"
      onClick={(e) => {
        onClick(e, page);
      }}
      classes={['pagination__btn', ownClasses['pagination-button']]}
      aria-label={`Andare a pagina ${page}`}
    >
      {page.toString()}
    </Button>
  );
};

const CurrentPage: React.FunctionComponent<{ children: string }> = ({
  children,
}) => {
  return (
    <p
      className={classnames(
        'button-text',
        ownClasses['current-page'],
        ownClasses['button-like']
      )}
    >
      {children}
    </p>
  );
};

const PaginationComponent: React.FunctionComponent<Props> = ({
  lastPage,
  currentPage,
  changePage,
}) => {
  const handleSelection: PageClick = (
    e: React.FormEvent,
    selectedPage: number
  ) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    changePage(selectedPage);
  };

  if (
    lastPage === 1 ||
    currentPage > lastPage ||
    currentPage > LISTING_PAGE_LIMIT
  ) {
    return null;
  }

  const onFirstPage = currentPage === 1;
  const onLastPage =
    currentPage === lastPage || currentPage === LISTING_PAGE_LIMIT;

  const previousPage: number | null = onFirstPage ? null : currentPage - 1;
  const nextPage: number | null = onLastPage ? null : currentPage + 1;

  return (
    <nav
      className={classnames(
        'pagination-container',
        ownClasses['pagination-container']
      )}
    >
      <PaginationArrow
        direction={ButtonDirection.PREV}
        disabled={onFirstPage}
        onClick={handleSelection}
        page={1}
      />

      {onFirstPage ? null : <PageNumber onClick={handleSelection} page={1} />}

      {previousPage && previousPage > 2 ? <Placeholder /> : null}

      {previousPage !== null && currentPage !== 2 ? (
        <PageNumber onClick={handleSelection} page={previousPage} />
      ) : null}

      <CurrentPage>{currentPage.toString()}</CurrentPage>

      {nextPage !== null ? (
        <PageNumber onClick={handleSelection} page={nextPage} />
      ) : null}

      <PaginationArrow
        direction={ButtonDirection.NEXT}
        disabled={onLastPage}
        onClick={handleSelection}
        page={nextPage}
      />
    </nav>
  );
};

const mapStateToProps = (
  state: Pick<RootState, 'search' | 'items'>
): StateProps => {
  return {
    currentPage: state.search.page,
    lastPage: state.items.totalPages,
  };
};

const PaginationSelector = connect<
  StateProps,
  DispatchProps,
  unknown,
  RootState
>(mapStateToProps, (dispatch) => ({
  changePage: (page: number) => {
    dispatch(updatePage(page));
  },
}))(PaginationComponent);

export default PaginationSelector;
export { PaginationComponent, mapStateToProps };
